import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import styles from './SourceFileSummary.module.css';

const EMPTY_SERIAL_NUMBERS = Object.freeze([]);

const API_ORIGIN =
  process.env.REACT_APP_API_BASE ||
  (['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:3001'
    : '');

const normalizedApiOrigin = API_ORIGIN.replace(/\/$/, '');
const API_BASE = normalizedApiOrigin.endsWith('/api')
  ? normalizedApiOrigin
  : `${normalizedApiOrigin}/api`;

function normalizeDate(date) {
  if (!date) return '';
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return date.toISOString().split('T')[0];
  }
  return String(date);
}

function normalizeSerialNumbers(serialNumbers) {
  if (!Array.isArray(serialNumbers)) return [];

  return serialNumbers
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item?.value) return String(item.value).trim();
      if (item?.serialNumber) return String(item.serialNumber).trim();
      return '';
    })
    .filter(Boolean);
}

export default function SourceFileSummary({
  startDate = null,
  endDate = null,
  ateSwVersion = '',
  testspec = '',
  serialNumbers = EMPTY_SERIAL_NUMBERS,
  title = 'Source files used for dashboard charts',
  className = '',
  actionContent = null,
  compact = false,
  selectedFamilies = EMPTY_SERIAL_NUMBERS,
  onFamiliesChange = null,
}) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState({ families: [], totalFamilies: 0, totalFiles: 0 });
  const [loading, setLoading] = useState(true);
  const serialNumbersParam = useMemo(
    () => normalizeSerialNumbers(serialNumbers).join(','),
    [serialNumbers]
  );
  const activeFamilies = useMemo(
    () => normalizeSerialNumbers(selectedFamilies),
    [selectedFamilies]
  );

  const queryParams = useMemo(() => {
    const params = {};
    const start = normalizeDate(startDate);
    const end = normalizeDate(endDate);

    if (start) params.startDate = start;
    if (end) params.endDate = end;
    if (ateSwVersion) params.ateSwVersion = ateSwVersion;
    if (testspec) params.testspec = testspec;
    if (serialNumbersParam) params.serialNumbers = serialNumbersParam;
    params.limit = compact ? 12 : 8;
    return params;
  }, [ateSwVersion, compact, endDate, serialNumbersParam, startDate, testspec]);

  useEffect(() => {
    let active = true;

    const loadSummary = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE}/file-family-summary`, { params: queryParams });
        if (!active) return;
        setSummary({
          families: Array.isArray(response.data?.families) ? response.data.families : [],
          totalFamilies: Number(response.data?.totalFamilies) || 0,
          totalFiles: Number(response.data?.totalFiles) || 0,
        });
      } catch (error) {
        if (!active) return;
        setSummary({ families: [], totalFamilies: 0, totalFiles: 0 });
      } finally {
        if (active) setLoading(false);
      }
    };

    loadSummary();
    return () => {
      active = false;
    };
  }, [queryParams]);

  const extraFamilies = Math.max(summary.totalFamilies - summary.families.length, 0);
  const cardClassName = [
    styles.summaryCard,
    compact ? styles.compactCard : '',
    className,
  ].filter(Boolean).join(' ');

  const handleFamilyToggle = (familyName) => {
    if (!onFamiliesChange) return;
    const nextFamilies = activeFamilies.includes(familyName)
      ? activeFamilies.filter((item) => item !== familyName)
      : [...activeFamilies, familyName];
    onFamiliesChange(nextFamilies);
  };

  const handleClearFamilies = () => {
    if (!onFamiliesChange) return;
    onFamiliesChange([]);
  };

  const titleText = loading
    ? t('Checking uploaded sources...')
    : summary.totalFiles
      ? t('{{count}} file(s) in view', { count: summary.totalFiles })
      : t('No uploaded files in view');

  return (
    <section className={cardClassName}>
      {compact ? (
        <div className={styles.compactRow}>
          <span className={styles.compactLabel}>{title}</span>
          <span className={styles.compactValue}>{titleText}</span>

          {loading && <span className={styles.loadingChip}>{t('Loading…')}</span>}

          {!loading && summary.families.map((family) => (
            <button
              type="button"
              key={family.family}
              className={`${styles.familyChip} ${activeFamilies.includes(family.family) ? styles.familyChipActive : ''}`}
              title={family.sampleFilename || family.family}
              onClick={() => handleFamilyToggle(family.family)}
              aria-pressed={activeFamilies.includes(family.family)}
            >
              {family.family}
            </button>
          ))}

          {!loading && extraFamilies > 0 && (
            <span className={styles.moreChip}>{t('+{{count}} more', { count: extraFamilies })}</span>
          )}

          {!loading && summary.totalFiles === 0 && (
            <span className={styles.emptyChip}>{t('Upload files to see source families here.')}</span>
          )}

          {!loading && summary.totalFamilies > 0 && (
            <span className={styles.countPill}>
              {t('{{count}} family(ies)', { count: summary.totalFamilies })}
            </span>
          )}

          {!loading && activeFamilies.length > 0 && (
            <button type="button" className={styles.clearChip} onClick={handleClearFamilies}>
              {t('Clear families')}
            </button>
          )}

          {actionContent && (
            <div className={styles.compactAction}>
              {actionContent}
            </div>
          )}
        </div>
      ) : (
        <>
      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>{title}</p>
          <h3 className={styles.title}>{titleText}</h3>
        </div>
        {!loading && summary.totalFamilies > 0 && (
          <span className={styles.countPill}>
            {t('{{count}} family(ies)', { count: summary.totalFamilies })}
          </span>
        )}
      </div>

      {actionContent && (
        <div className={styles.actionRow}>
          {actionContent}
        </div>
      )}

      <div className={styles.chipRow}>
        {loading && <span className={styles.loadingChip}>{t('Loading…')}</span>}

        {!loading && summary.families.map((family) => (
          <button
            type="button"
            key={family.family}
            className={`${styles.familyChip} ${activeFamilies.includes(family.family) ? styles.familyChipActive : ''}`}
            title={family.sampleFilename || family.family}
            onClick={() => handleFamilyToggle(family.family)}
            aria-pressed={activeFamilies.includes(family.family)}
          >
            {family.family}
          </button>
        ))}

        {!loading && extraFamilies > 0 && (
          <span className={styles.moreChip}>{t('+{{count}} more', { count: extraFamilies })}</span>
        )}

        {!loading && summary.totalFiles === 0 && (
          <span className={styles.emptyChip}>{t('Upload files to see source families here.')}</span>
        )}

        {!loading && activeFamilies.length > 0 && (
          <button type="button" className={styles.clearChip} onClick={handleClearFamilies}>
            {t('Clear families')}
          </button>
        )}
      </div>
        </>
      )}
    </section>
  );
}
